/**
 * Module: `formatting/actions.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import type { LucideIcon } from "lucide-react";
import {
  Search,
  Globe,
  Database,
  Play,
  Settings,
  BarChart3,
  FileText,
  Server,
  Code,
  MessageSquare,
  GitGraph,
  Wrench,
  FunctionSquare,
  Brain,
  Zap,
} from "lucide-react";
import type { ChainOfThoughtAction, DustAction } from "@/types";

/**
 * Mapping of Dust action types to human-readable French labels and icons
 */
const ACTION_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  // Core retrieval/search actions
  retrieval_action: { label: "Recherche dans les documents", icon: "search" },
  search_action: { label: "Recherche sémantique", icon: "search" },

  // Include/data actions (Dust MCP)
  include_data_action: { label: "Récupération de données", icon: "file" },

  // Web actions
  websearch_action: { label: "Recherche web", icon: "globe" },
  browse_action: { label: "Navigation web", icon: "globe" },

  // Data/tables actions
  tables_query_action: { label: "Requête sur les tables", icon: "database" },
  query_tables_action: { label: "Requête sur les tables", icon: "database" },
  query_tables_v2_action: { label: "Requête SQL", icon: "database" },

  // App actions
  dust_app_run_action: { label: "Exécution d'application Dust", icon: "play" },

  // Processing actions
  process_action: { label: "Traitement de données", icon: "cog" },

  // Visualization actions
  visualization_action: { label: "Visualisation de données", icon: "chart" },

  // File actions
  conversation_include_file_action: {
    label: "Inclusion de fichier",
    icon: "file",
  },

  // MCP actions (Model Context Protocol)
  mcp_action: { label: "Action MCP", icon: "server" },

  // Reasoning actions
  reasoning_action: { label: "Raisonnement", icon: "brain" },

  // Code actions
  code_interpreter_action: { label: "Interpréteur de code", icon: "code" },

  // Agent actions
  agent_message_action: { label: "Message de l'agent", icon: "message" },

  // GitHub actions
  github_create_issue_action: {
    label: "Création d'issue GitHub",
    icon: "github",
  },
  github_get_pull_request_action: {
    label: "Récupération PR GitHub",
    icon: "github",
  },

  // Slack actions
  slack_send_message_action: { label: "Envoi message Slack", icon: "slack" },

  // Generic fallbacks
  tool_action: { label: "Utilisation d'outil", icon: "wrench" },
  function_call_action: { label: "Appel de fonction", icon: "function" },
};

/**
 * Extract the action type from various possible locations in the action object
 * Dust MCP actions use internalMCPServerName and functionCallName instead of type
 */
export function extractActionType(
  action: DustAction | null | undefined
): string {
  if (!action) return "";

  // Direct type property (standard actions)
  if (action.type && typeof action.type === "string") {
    return action.type;
  }

  // Dust MCP action: use internalMCPServerName
  if (
    action.internalMCPServerName &&
    typeof action.internalMCPServerName === "string"
  ) {
    const serverName = action.internalMCPServerName.toLowerCase();

    // Map MCP server names to action types
    if (serverName === "search" || serverName.includes("search")) {
      return "retrieval_action";
    }
    if (serverName === "include_data" || serverName.includes("include")) {
      return "include_data_action";
    }
    if (
      serverName === "query_tables" ||
      serverName === "query_tables_v2" ||
      serverName.includes("table")
    ) {
      return "tables_query_action";
    }
    if (serverName === "websearch" || serverName.includes("web")) {
      return "websearch_action";
    }
    if (serverName === "browse" || serverName.includes("browse")) {
      return "browse_action";
    }
    if (serverName === "dust_app_run" || serverName.includes("app")) {
      return "dust_app_run_action";
    }
    if (serverName === "process" || serverName.includes("process")) {
      return "process_action";
    }
    if (serverName === "visualization" || serverName.includes("visual")) {
      return "visualization_action";
    }

    // Return a formatted version of the server name
    return `${serverName}_action`;
  }

  // Try to extract from functionCallName (e.g. "include_data__retrieve_recent_documents")
  if (action.functionCallName && typeof action.functionCallName === "string") {
    const funcName = action.functionCallName.toLowerCase();

    if (funcName.includes("search") || funcName.includes("semantic")) {
      return "retrieval_action";
    }
    if (funcName.includes("include") || funcName.includes("retrieve")) {
      return "include_data_action";
    }
    if (
      funcName.includes("table") ||
      funcName.includes("query") ||
      funcName.includes("database") ||
      funcName.includes("schema")
    ) {
      return "tables_query_action";
    }
    if (funcName.includes("websearch") || funcName.includes("web_search")) {
      return "websearch_action";
    }
    if (funcName.includes("browse") || funcName.includes("navigate")) {
      return "browse_action";
    }

    // Extract the first part of the function name (before __)
    const parts = action.functionCallName.split("__");
    if (parts.length > 0) {
      return `${parts[0].toLowerCase()}_action`;
    }
  }

  // actionType property
  if (action.actionType && typeof action.actionType === "string") {
    return action.actionType;
  }

  // Check for specific action patterns (legacy)
  if (action.retrieval || action.retrievalAction) {
    return "retrieval_action";
  }
  if (action.websearch || action.webSearch || action.websearchAction) {
    return "websearch_action";
  }
  if (action.browse || action.browseAction) {
    return "browse_action";
  }
  if (action.tablesQuery || action.tables_query || action.tablesQueryAction) {
    return "tables_query_action";
  }

  // Log unknown action structure for debugging
  console.warn("Could not extract action type. Keys:", Object.keys(action));

  return "";
}

/**
 * Extract action type from a ChainOfThoughtAction (sidebar/streaming steps).
 * Uses type, metadata.type, or content-based inference.
 */
export function getActionTypeFromChainOfThoughtAction(
  action: ChainOfThoughtAction
): string {
  if (action.type && action.type !== "action" && action.type !== "conclusion") {
    return action.type;
  }
  if (action.metadata?.type && typeof action.metadata.type === "string") {
    return action.metadata.type;
  }
  const content = action.content.toLowerCase();
  if (
    content.includes("recherche dans les documents") ||
    content.includes("retrieval")
  ) {
    return "retrieval_action";
  }
  if (content.includes("recherche web") || content.includes("websearch")) {
    return "websearch_action";
  }
  if (
    content.includes("table") ||
    content.includes("requête") ||
    content.includes("query")
  ) {
    return "tables_query_action";
  }
  return "tool_action";
}

/**
 * Get human-readable label for an action type
 */
export function getActionLabel(
  actionType: string,
  action?: DustAction
): string {
  // Check if we have a known mapping
  const mapping = ACTION_TYPE_LABELS[actionType];
  let baseLabel = "";

  if (mapping) {
    baseLabel = mapping.label;
  } else if (actionType) {
    // Try to extract a meaningful name from the action type itself
    // Remove common suffixes and format
    baseLabel = actionType
      .replace(/_action$/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // If action has functionCallName, extract source information for better context
  if (action?.functionCallName) {
    const funcName = action.functionCallName;
    const parts = funcName.split("__");

    if (parts.length >= 1) {
      // Format the source part (first part before __)
      let sourcePart = parts[0]
        .replace(/_/g, " ")
        .replace(
          /all the attachements related to the table piece/i,
          "Pièces jointes"
        )
        .replace(
          /documentation cncc formation legifrance/i,
          "Documentation CNCC"
        )
        .replace(/normes comptables/i, "Normes comptables")
        .replace(/bdd documentaire sidoni/i, "Base documentaire Sidoni")
        .replace(/include data/i, "Données");

      // Capitalize first letter
      sourcePart = sourcePart.charAt(0).toUpperCase() + sourcePart.slice(1);

      if (baseLabel) {
        return `${baseLabel}: ${sourcePart}`;
      }
      return sourcePart;
    }
  }

  // If action has internalMCPServerName, use it as context
  if (!baseLabel && action?.internalMCPServerName) {
    const serverName = action.internalMCPServerName;
    const serverLabel = serverName
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
    return serverLabel;
  }

  // If action has a name property, use it
  if (!baseLabel && action?.name) {
    return action.name;
  }

  // Return baseLabel or fallback
  if (baseLabel) {
    return baseLabel;
  }

  return "Action";
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}...` : s;
}

/** Dust / MCP action types that run SQL against tables — never show the query text. */
const TABLES_QUERY_ACTION_TYPES = new Set([
  "tables_query_action",
  "query_tables_action",
  "query_tables_v2_action",
]);

export function isTablesQueryActionType(actionType: string): boolean {
  return TABLES_QUERY_ACTION_TYPES.has(actionType);
}

function extractDetailSuffix(action: DustAction): string {
  const query: string =
    (action?.params?.query as string) || (action?.query as string) || "";
  if (query) return `: "${truncate(query, 50)}"`;

  const url: string =
    (action?.url as string) || (action?.params?.url as string) || "";
  if (url) return `: ${truncate(url, 60)}`;

  const name: string =
    (action?.appName as string) ||
    (action?.params?.appName as string) ||
    (action?.toolName as string) ||
    (action?.name as string) ||
    "";
  if (name) return `: ${name}`;

  return "";
}

export type FormatActionForDisplayOptions = {
  /**
   * When true, appends query, URL, or app/tool name from the action.
   * Ignored for tables-query action types (SQL is never shown).
   */
  includeDetailSuffix?: boolean;
};

/**
 * Format an action for display — returns the human-readable content string.
 * Tables-query actions keep label (+ source) only; SQL / query detail is omitted.
 */
export function formatActionForDisplay(
  actionType: string,
  action: DustAction,
  options?: FormatActionForDisplayOptions
): string {
  const label = getActionLabel(actionType, action);
  if (options?.includeDetailSuffix && !isTablesQueryActionType(actionType)) {
    return label + extractDetailSuffix(action);
  }
  return label;
}

/**
 * Get icon component for an action type
 * Returns the icon component (not JSX) for framework-agnostic usage
 */
export function getActionIcon(actionType: string): LucideIcon {
  // Map action types to icon components
  const iconMap: Record<string, LucideIcon> = {
    retrieval_action: Search,
    search_action: Search,
    include_data_action: FileText,
    websearch_action: Globe,
    browse_action: Globe,
    tables_query_action: Database,
    query_tables_action: Database,
    query_tables_v2_action: Database,
    dust_app_run_action: Play,
    process_action: Settings,
    visualization_action: BarChart3,
    conversation_include_file_action: FileText,
    mcp_action: Server,
    reasoning_action: Brain,
    code_interpreter_action: Code,
    agent_message_action: MessageSquare,
    github_create_issue_action: GitGraph,
    github_get_pull_request_action: GitGraph,
    slack_send_message_action: MessageSquare,
    tool_action: Wrench,
    function_call_action: FunctionSquare,
  };

  return iconMap[actionType] || Zap;
}
